import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import type { AnalysisRegion, PhotoPurposeInUse } from '@safetyhub/shared';
import type { PendingPhoto } from '../components/inspection/shared/PhotoUploadSheet';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores/auth';
import { usePermissions } from './usePermissions';
import { useWebSocketStore } from '../stores/websocket';
import { useModalStore } from '../stores/modal';
import { apiErrorMessage, extractApiError, normalizeFileUrl } from '../lib/api';
import { hasSelectedMeasure } from '../lib/findingVisibility';
import {
  useInspection,
  usePreviousUnresolvedFindings,
  useIsAnalysisComplete,
  useContributors,
  useJoinInspection,
  useResolveWithEvidence,
  useUpdatePhotoReview,
  useBulkReviewPhotos,
  useUpdateFinding,
  useDeleteFinding,
  useRestoreFinding,
  useAddMeasure,
  useRemoveMeasure,
  useAddLegalBasis,
  useRemoveLegalBasis,
} from './useInspections';
import {
  uploadPhotosParallel,
  deleteInspection,
  deletePhoto,
  deletePhotos,
  updateInspection,
  reanalyzePhoto,
  bulkReanalyzePhotos,
  unresolveFinding,
  toggleMeasureSelection,
  startScheduledInspection,
  todayScheduledQueryRootKey,
  requestReview as apiRequestReview,
  addFinding as apiAddFinding,
  InspectionFinding,
  PreviousUnresolvedFinding,
  AIAnalysisDetail,
  UpdateFindingInput,
  ReviewRequestResult,
  UploadProgress,
  InspectionCollaborator,
} from '../lib/inspections';
import { useInspectionCompletion } from './useInspectionCompletion';
import {
  ServerEvents,
  InspectionViewer,
  InspectionViewersListPayload,
  InspectionDeletedPayload,
  InspectionInspectorChangedPayload,
  ReportCreatedNotifyPayload,
  ReportDeletedNotifyPayload,
  FindingMeasureSelectionUpdatedPayload,
} from '../types/websocket';
import { useToastStore } from '../stores/toast';
import { getEvidenceDisplayName } from '@safetyhub/shared';
import type { RiskAssessmentConfig } from '@safetyhub/shared';
// 레이아웃 컴포넌트와 호환되는 타입 정의
type PhotoStatus = 'UPLOADING' | 'COMPLETED' | 'FAILED';
type LayoutAnalysisStatus = 'PENDING' | 'ANALYZING' | 'COMPLETED' | 'FAILED';
type Severity = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL' | 'GOOD';
type InspectionStatus = 'SCHEDULED' | 'IN_PROGRESS' | 'PENDING_REVIEW' | 'COMPLETED';

interface LayoutFinding {
  id: string;
  category: string;
  subCategory?: string;
  description: string;
  severity: Severity;
  aiGenerated: boolean;
  aiConfidence?: number;
  aiAnalysisDetail?: AIAnalysisDetail;
  aiEdited?: boolean;
  aiLikelihoodScore?: number | null;
  aiSeverityScore?: number | null;
  // TBM 분류 축 (규약 ③) — 분석 불변값 / 사람 확정값
  analyzedWorkCategory?: string | null;
  confirmedWorkCategory?: string | null;
  modifications?: {
    isModified: boolean;
    modifiedFields: Array<{
      field: string;
      originalValue: unknown;
      currentValue: unknown;
    }>;
  };
  // 해소 관련
  resolutionStatus?: 'PENDING' | 'COMPLIED' | 'NON_COMPLIED' | 'PARTIAL';
  resolutionPhotoUrl?: string;
  resolutionPhotos?: Array<{ url: string; thumbnailUrl?: string; fileId?: string }>;
  notes?: string;
  // Soft delete
  deletedAt?: string;
  deletedBy?: string;
  // 선택된 개선대책 목록 (각 항목 선택한 사용자 정보 포함)
  selectedMeasures?: Array<{
    measure: string;
    selectedById: string;
    selectedByName: string | null;
    selectedByRole: 'OWNER' | 'PRIMARY' | 'SECONDARY' | null;
    selectedByCompletionStatus?: 'IN_PROGRESS' | 'REVIEWING' | 'COMPLETED' | null;
  }>;
}

interface LayoutPhoto {
  id: string;
  url: string;
  thumbnailUrl?: string;
  status: PhotoStatus;
  analysisStatus: LayoutAnalysisStatus;
  // ⚠️ `analysisSummary`·`analysisError`를 **여기 다시 선언하지 말 것**(2026-10-05 삭제).
  //
  //    아래 `convertPhotosToLayout`이 둘을 **채우지 않았고** 읽는 곳도 0건이었다 — 바로 위
  //    `analysisRegion`과 **정확히 같은 누락**인데 optional이라 tsc가 침묵했고, 선언이 세 층
  //    (여기 · `components/inspection/layouts/inspection-detail.types.ts` · `PhotoCardVertical`
  //    props)에 있어 「끝에서 끝까지 배선된」 것처럼 보였다. 사유를 안 보이는 근거는
  //    `PhotoCardVertical`의 「분석에 실패했습니다.」 자리 주석이 소유한다.
  //
  //    ⚠️ 줄 주석인 것이 의도다 — `/** */`로 쓰면 **아래 `findings`의 JSDoc이 되어** IDE hover에
  //    엉뚱한 설명으로 뜬다(2026-10-06 리뷰 적발). 이 파일의 `contributorId` 묘비도 줄 주석이다.
  findings: LayoutFinding[];
  isReviewed: boolean;
  /**
   * 중점 분석 영역 — `{x,y,w,h}` 정규화 0~1. 없으면 사진 전체를 분석한다.
   *
   * ⚠️ `LayoutFinding.boundingBox`는 `{x,y,width,height}`라 **어휘가 다르다** — 섞으면 서버가
   * 불량으로 판정해 경고만 남기고 전체 분석으로 조용히 떨어진다(결정 2026-10-05).
   *
   * ⚠️ **optional(`?`)로 두지 말 것 — 이 자리가 그래서 한 번 비었다**(2026-10-05 dev 실측).
   * 아래 `convertPhotosToLayout`이 사진을 **필드별로 손나열**하는데 이 필드를 빠뜨렸고,
   * optional이라 tsc가 침묵했다. 증상은 「범위 지정 후 다시 분석」이 저장된 영역을 못 불러오는
   * 것이고, 더 나쁘게는 **그대로 확인을 누르면 `null`이 전달돼 지정해 둔 영역이 지워진다**
   * (실측으로 재현 — 선반 영역이 `NULL`이 됐다). required로 두면 같은 누락이 tsc 에러가 된다.
   */
  analysisRegion: AnalysisRegion | null;
  /** 사진 업로더 ID (권한 체크용) */
  addedById?: string;
  /** 사진 업로더 정보 (등록자 표시용) */
  addedBy?: {
    id: string;
    name: string;
  };
  /**
   * ⚠️ DB enum보다 좁다 — 「오늘 쓰는 용도」다. 되살릴 때 볼 것은 `@safetyhub/shared`의
   * `PHOTO_PURPOSES_DORMANT` JSDoc의 「되살릴 때 해야 할 일」이다.
   */
  purpose?: PhotoPurposeInUse;
  /** 장소 */
  location?: { id: string; name: string } | null;
  /** 상세 위치 */
  detailLocation?: { id: string; name: string } | null;
  /** 대상 (설비/기계 등) */
  equipment?: { id: string; name: string; managementNumber?: string | null; processType?: string | null } | null;
  /** 사진에 직접 저장된 공종/공정 */
  processType?: string | null;
}

interface LayoutPreviousFinding {
  id: string;
  category: string;
  subCategory?: string;
  description: string;
  severity: Severity;
  photoUrl?: string;
  thumbnailUrl?: string;
  foundAt: string;
  resolutionStatus: 'PENDING' | 'COMPLIED' | 'NON_COMPLIED' | 'PARTIAL';
  resolutionPhotoUrl?: string;
  resolutionPhotos?: Array<{ url: string; thumbnailUrl?: string; fileId?: string }>;
  notes?: string;
  resolvedAt?: string;
  lastActionInspectionId?: string;
  /** 사진 업로더 ID (권한 체크용) */
  photoAddedById?: string;
  /** 원래 점검 ID (어노테이션 조회용) */
  sourceInspectionId?: string;
  /** 원래 사진 ID (어노테이션 조회용) */
  photoId?: string;
  /** AI 분석 상세 (이전 미해결 상세 모달용) */
  aiAnalysisDetail?: AIAnalysisDetail;
  aiLikelihoodScore?: number | null;
  aiSeverityScore?: number | null;
  /** 보고서 최종 개선대책 */
  reportMeasure?: string;
  /** 보고서 최종 위험요인 */
  reportRisk?: string;
}

interface LayoutContributor {
  id: string;
  userId: string;
  userName: string;
  role: 'PRIMARY' | 'SECONDARY';
  profileImageUrl?: string;
  // ⚠️ `contributorId`를 다시 넣지 말 것(2026-10-05 삭제) — `convertContributorsToLayout`이
  //    채우지 않는 죽은 선언이었고, 참여자 식별자는 위 `id`(= `InspectionParticipant.id`)
  //    하나다. 둘이 나란히 있으면 어느 쪽이 참여자인지 헷갈린다. web의 다른 `contributorId`
  //    히트 3건은 다른 인터페이스다(WS 알림 payload 키 2 + `ExcludeContributorInput`).
  completionStatus?: 'IN_PROGRESS' | 'COMPLETED';
  completedAt?: string;
  signerName?: string;
  signatureData?: string;
  excludedAt?: string;
  hasPhotoContribution?: boolean;
  hasMeasureContribution?: boolean;
}

interface LayoutInspectionDetail {
  id: string;
  siteName: string;
  siteId: string;
  status: InspectionStatus;
  createdById?: string;
  inspectorId: string;
  inspectorName: string;
  contributors: LayoutContributor[];
  collaborators?: InspectionCollaborator[];
  photos: LayoutPhoto[];
  previousFindings: LayoutPreviousFinding[];
  createdAt: string;
  scheduledAt?: string;
  assignedById?: string | null;
  nextInspectionDate?: string | null;
  nextInspectionHasReport?: boolean;
  blockingInspectionId?: string;
  blockingInspectionOrder?: number;
  /** 점검 차수 (서버 findOne 응답에서 제공) */
  sequenceNumber?: number;
  effectiveRiskAssessmentConfig?: RiskAssessmentConfig | null;
}

export async function createStablePreview(file: File): Promise<PendingPhoto> {
  const buffer = await file.arrayBuffer();
  const stableBlob = new Blob([buffer], { type: file.type });
  return { file, preview: URL.createObjectURL(stableBlob) };
}

/**
 * 점검 상세 페이지 데이터 훅
 * AdaptiveLayout과 함께 사용하기 위한 통합 데이터 훅
 */
export function useInspectionDetailPage(inspectionId: string | undefined) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { currentMembership, user } = useAuthStore();
  const { subscribe, isConnected, isAuthenticated, joinInspection: wsStoreJoinInspection, leaveInspection: wsStoreLeaveInspection } = useWebSocketStore();
  const { openResolutionPhoto, openCameraCapture } = useModalStore();
  const { showToast } = useToastStore();
  const companyId = currentMembership?.companyId;
  const currentUserId = user?.id || '';
  // 로컬 상태
  const [error, setError] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isReanalyzing, setIsReanalyzing] = useState(false);
  // AI 분석 활성화 여부 (기본값: true)
  const [enableAIAnalysis, setEnableAIAnalysis] = useState(true);
  // AI 분석 중점 카테고리 (빈 배열 = 전체 분석)
  const [focusCategories, setFocusCategoriesLocal] = useState<string[]>([]);
  const [focusCategoriesInitialized, setFocusCategoriesInitialized] = useState(false);

  // 대기 중 사진 목록 (선택 → 메타 설정 → 업로드 흐름)
  const [pendingPhotos, setPendingPhotos] = useState<PendingPhoto[]>([]);
  const [photoUploadSheetOpen, setPhotoUploadSheetOpen] = useState(false);

  // 카테고리 변경 시 서버에도 저장
  const setFocusCategories = useCallback((categories: string[]) => {
    setFocusCategoriesLocal(categories);
    if (inspectionId && companyId) {
      updateInspection(inspectionId, { focusCategories: categories }, companyId).catch(() => {});
    }
  }, [inspectionId, companyId]);

  // 현재 점검을 보고 있는 사용자 목록
  const [viewers, setViewers] = useState<InspectionViewer[]>([]);

  // React Query 훅들
  const {
    data: inspection,
    isLoading,
    error: queryError,
  } = useInspection(inspectionId, companyId);

  // 점검 데이터에서 focusCategories 초기값 동기화
  useEffect(() => {
    if (inspection && !focusCategoriesInitialized) {
      const saved = inspection.focusCategories;
      if (Array.isArray(saved) && saved.length > 0) {
        setFocusCategories(saved);
      }
      setFocusCategoriesInitialized(true);
    }
  }, [inspection, focusCategoriesInitialized]);

  const { data: previousFindings = [] } = usePreviousUnresolvedFindings(
    inspectionId,
    companyId
  );

  const { data: isAnalysisComplete = false } = useIsAnalysisComplete(
    inspectionId,
    companyId
  );

  const { data: contributors = [] } = useContributors(inspectionId, companyId);

  // Mutation 훅들
  const joinInspectionMutation = useJoinInspection(companyId);
  const resolveWithEvidenceMutation = useResolveWithEvidence(inspectionId, companyId);
  const updatePhotoReviewMutation = useUpdatePhotoReview(inspectionId, companyId);
  const bulkReviewPhotosMutation = useBulkReviewPhotos(inspectionId, companyId);

  // 발견사항 편집 훅들
  const updateFindingMutation = useUpdateFinding(inspectionId, companyId);
  const deleteFindingMutation = useDeleteFinding(inspectionId, companyId);
  const restoreFindingMutation = useRestoreFinding(inspectionId, companyId);
  const addMeasureMutation = useAddMeasure(inspectionId, companyId);
  const removeMeasureMutation = useRemoveMeasure(inspectionId, companyId);
  const addLegalBasisMutation = useAddLegalBasis(inspectionId, companyId);
  const removeLegalBasisMutation = useRemoveLegalBasis(inspectionId, companyId);

  // WebSocket 재연결 감지를 위한 ref
  const wasConnectedRef = useRef(false);

  // WebSocket Room 입장/퇴장 및 viewers 관리
  useEffect(() => {
    if (!inspectionId || !isConnected || !isAuthenticated) {
      return;
    }

    // 재연결된 경우 데이터 새로고침 (연결이 끊겼다가 다시 연결된 경우)
    if (wasConnectedRef.current) {
      if (import.meta.env.DEV) console.log('[WebSocket] Reconnected, refreshing inspection data...');
      queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId] });
    }
    wasConnectedRef.current = true;

    // Room 입장 및 초기 viewers 설정
    // store의 joinInspection을 사용하여 currentInspectionId가 업데이트되도록 함
    // (재연결 시 AUTHENTICATED 핸들러에서 currentInspectionId로 재입장)
    wsStoreJoinInspection(inspectionId).then(() => {
      // viewers 초기값은 INSPECTION_VIEWERS_LIST 이벤트로 서버에서 전송됨
    });

    // viewers 목록 업데이트 이벤트 구독
    const unsubscribe = subscribe<InspectionViewersListPayload>(
      ServerEvents.INSPECTION_VIEWERS_LIST,
      (payload) => {
        if (payload.inspectionId === inspectionId) {
          setViewers(payload.viewers);
        }
      }
    );

    return () => {
      if (inspectionId) {
        wsStoreLeaveInspection(inspectionId);
        setViewers([]);
      }
      unsubscribe();
    };
  }, [inspectionId, isConnected, isAuthenticated, subscribe, queryClient, wsStoreJoinInspection, wsStoreLeaveInspection]);

  // WebSocket 연결 끊김 감지 - wasConnectedRef 업데이트
  useEffect(() => {
    if (!isConnected && wasConnectedRef.current) {
      if (import.meta.env.DEV) console.log('[WebSocket] Connection lost');
    }
  }, [isConnected]);

  // 점검 삭제 이벤트 구독 (다른 사용자가 삭제한 경우)
  useEffect(() => {
    if (!isConnected || !isAuthenticated || !inspectionId) return;

    const unsubscribe = subscribe<InspectionDeletedPayload>(
      ServerEvents.INSPECTION_DELETED,
      (payload) => {
        if (payload.inspectionId === inspectionId) {
          // 현재 보고 있는 점검이 삭제됨 - 스케줄로 이동
          const date = inspection?.scheduledAt?.split('T')[0];
          const month = date?.substring(0, 7);
          const params = new URLSearchParams();
          if (month) params.set('month', month);
          if (date) params.set('date', date);
          navigate(`/schedules?${params.toString()}`, { replace: true });
        }
      }
    );

    return () => unsubscribe();
  }, [inspectionId, isConnected, isAuthenticated, subscribe, navigate, inspection]);

  // 주 담당자 변경 이벤트 구독 (다른 사용자가 변경한 경우)
  useEffect(() => {
    if (!isConnected || !isAuthenticated || !inspectionId) return;

    const unsubscribe = subscribe<InspectionInspectorChangedPayload>(
      ServerEvents.INSPECTION_INSPECTOR_CHANGED,
      (payload) => {
        if (payload.inspectionId === inspectionId) {
          // 점검 데이터 캐시 무효화
          queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId] });

          // 토스트 알림 표시
          showToast(
            `${payload.changedBy.name}님이 주 담당자를 ${payload.newInspector.name}님으로 변경했습니다.`,
            'info'
          );
        }
      }
    );

    return () => unsubscribe();
  }, [inspectionId, isConnected, isAuthenticated, subscribe, queryClient, showToast]);

  // 보고서 생성 이벤트 구독 (해당 점검에 보고서가 생성되면 사진 추가 불가)
  // 토스트는 WebSocketProvider의 handleReportCreatedNotify에서 통합 처리됨
  useEffect(() => {
    if (!isConnected || !isAuthenticated || !inspectionId) return;

    const unsubscribe = subscribe<ReportCreatedNotifyPayload>(
      ServerEvents.REPORT_CREATED_NOTIFY,
      (payload) => {
        if (payload.inspectionId === inspectionId) {
          // 점검 데이터 캐시 무효화 (hasReport 상태 갱신)
          queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId] });
        }
      }
    );

    return () => unsubscribe();
  }, [inspectionId, isConnected, isAuthenticated, subscribe, queryClient]);

  // 보고서 삭제 이벤트 구독 (해당 점검의 보고서가 삭제되면 사진 추가 가능)
  // 토스트는 WebSocketProvider의 handleReportDeletedNotify에서 통합 처리됨
  useEffect(() => {
    if (!isConnected || !isAuthenticated || !inspectionId) return;

    const unsubscribe = subscribe<ReportDeletedNotifyPayload>(
      ServerEvents.REPORT_DELETED_NOTIFY,
      (payload) => {
        if (payload.inspectionId === inspectionId) {
          // 점검 데이터 캐시 무효화 (hasReport 상태 갱신)
          queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId] });
        }
      }
    );

    return () => unsubscribe();
  }, [inspectionId, isConnected, isAuthenticated, subscribe, queryClient]);

  // 발견사항 개선대책 선택 상태 변경 이벤트 구독 (다른 사용자가 토글한 경우)
  useEffect(() => {
    if (!isConnected || !isAuthenticated || !inspectionId) return;

    const unsubscribe = subscribe<FindingMeasureSelectionUpdatedPayload>(
      ServerEvents.FINDING_MEASURE_SELECTION_UPDATED,
      (payload) => {
        if (payload.inspectionId !== inspectionId) return;
        // 본인이 토글한 이벤트는 mutation 응답으로 이미 반영됨
        if (payload.updatedBy.id === currentUserId) return;
        queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId] });
      }
    );

    return () => unsubscribe();
  }, [inspectionId, isConnected, isAuthenticated, subscribe, queryClient, currentUserId]);

  // URL 파라미터로 자동 참여 처리
  useEffect(() => {
    const shouldJoin = searchParams.get('join') === 'true';
    if (shouldJoin && inspectionId && companyId && !joinInspectionMutation.isPending) {
      joinInspectionMutation.mutate(inspectionId);
    }
  }, [searchParams, inspectionId, companyId, joinInspectionMutation]);

  // 에러 메시지 통합
  const displayError = error || (queryError instanceof Error ? queryError.message : '');

  // === 핸들러들 ===

  // 뒤로 가기
  const handleBack = useCallback(() => {
    navigate('/schedules');
  }, [navigate]);

  // 업로드 진행 상태
  const [uploadProgress, setUploadProgress] = useState<UploadProgress[]>([]);

  // 파일 선택 시 즉시 업로드하지 않고 pendingPhotos에 추가 후 시트 오픈
  const handlePhotoUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const validFiles: File[] = [];
    const errors: string[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!file.type.startsWith('image/')) {
        errors.push(`${file.name}: 이미지 파일이 아닙니다.`);
        continue;
      }
      if (file.size > 10 * 1024 * 1024) {
        errors.push(`${file.name}: 파일 크기가 10MB를 초과합니다.`);
        continue;
      }
      validFiles.push(file);
    }

    if (errors.length > 0) {
      setError(errors.join('\n'));
    }

    if (validFiles.length === 0) return;

    try {
      const newPending: PendingPhoto[] = [];
      for (const file of validFiles) {
        newPending.push(await createStablePreview(file));
      }
      setPendingPhotos((prev) => [...prev, ...newPending]);
      setPhotoUploadSheetOpen(true);
    } catch {
      showToast('사진을 읽을 수 없습니다. 다시 시도해주세요.', 'error');
    } finally {
      const input = e.target as HTMLInputElement;
      if (input) input.value = '';
    }
  }, [showToast]);

  // pendingPhotos를 실제로 업로드하는 핸들러 (PhotoUploadSheet에서 확정 시 호출)
  const handleConfirmUpload = useCallback(async (photos: PendingPhoto[]) => {
    if (photos.length === 0 || !inspectionId || !companyId || !inspection) return;

    setIsUploading(true);
    setError('');
    setUploadProgress([]);

    const perPhotoLocationIds = new Map<number, string | null>();
    const perPhotoEquipmentIds = new Map<number, string | null>();
    const perPhotoDetailLocationIds = new Map<number, string | null>();
    const perPhotoSkipAnalysis = new Map<number, boolean>();
    const perPhotoFocusCategories = new Map<number, string[]>();
    const perPhotoProcessTypes = new Map<number, string | null>();
    // 영역은 **점검 단위 폴백이 없다**(카테고리와 다른 점) — 사진마다 피사체 위치가 달라서
    // 한 사진의 좌표를 다른 사진에 쓰면 엉뚱한 자리가 잘린다(결정 2026-10-05).
    const perPhotoAnalysisRegions = new Map<number, AnalysisRegion | null>();
    photos.forEach((p, i) => {
      if (p.locationId !== undefined) perPhotoLocationIds.set(i, p.locationId ?? null);
      if (p.equipmentId !== undefined) perPhotoEquipmentIds.set(i, p.equipmentId ?? null);
      if (p.detailLocationId !== undefined) perPhotoDetailLocationIds.set(i, p.detailLocationId ?? null);
      if (p.processType !== undefined) perPhotoProcessTypes.set(i, p.processType ?? null);
      // skipAnalysis: PendingPhoto 기준 (undefined = 기본 enableAIAnalysis 따름)
      if (p.skipAnalysis !== undefined) {
        perPhotoSkipAnalysis.set(i, p.skipAnalysis);
      } else {
        perPhotoSkipAnalysis.set(i, !enableAIAnalysis);
      }
      if (p.focusCategories && p.focusCategories.length > 0) {
        perPhotoFocusCategories.set(i, p.focusCategories);
      }
      if (p.analysisRegion) perPhotoAnalysisRegions.set(i, p.analysisRegion);
    });

    try {
      const result = await uploadPhotosParallel(
        inspectionId,
        photos.map((p) => p.file),
        companyId,
        (progress) => setUploadProgress(progress),
        {
          perPhotoLocationIds,
          perPhotoEquipmentIds,
          perPhotoDetailLocationIds,
          perPhotoSkipAnalysis,
          perPhotoFocusCategories,
          perPhotoAnalysisRegions,
          perPhotoProcessTypes,
          // 전역 fallback (per-photo 미설정 시)
          skipAnalysis: !enableAIAnalysis,
          focusCategories: focusCategories.length > 0 ? focusCategories : undefined,
        }
      );

      if (result.success.length > 0) {
        queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId] });
      }

      if (result.failed.length > 0) {
        const uploadErrors = result.failed.map((f) => `${f.filename}: ${f.error}`);
        if (result.success.length > 0) {
          setError(`${result.success.length}개 업로드 완료. 오류: ${uploadErrors.join(', ')}`);
        } else {
          setError(uploadErrors.join('\n'));
        }
      }
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    }

    // 업로드 완료 후 preview URL 정리 및 시트 닫기
    photos.forEach((p) => URL.revokeObjectURL(p.preview));
    setPendingPhotos([]);
    setPhotoUploadSheetOpen(false);
    setIsUploading(false);
    setUploadProgress([]);
  }, [inspectionId, companyId, inspection, queryClient, enableAIAnalysis, focusCategories]);

  // 우수사례 사진 업로드 (복수 파일 지원 - 병렬 업로드)
  const handleBestPracticePhotoUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !inspectionId || !companyId || !inspection) return;

    const validFiles: File[] = [];
    const errors: string[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!file.type.startsWith('image/')) {
        errors.push(`${file.name}: 이미지 파일이 아닙니다.`);
        continue;
      }
      if (file.size > 10 * 1024 * 1024) {
        errors.push(`${file.name}: 파일 크기가 10MB를 초과합니다.`);
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length === 0) {
      setError(errors.join('\n') || '업로드할 유효한 이미지가 없습니다.');
      return;
    }

    setIsUploading(true);
    setError('');
    setUploadProgress([]);

    try {
      const result = await uploadPhotosParallel(
        inspectionId,
        validFiles,
        companyId,
        (progress) => setUploadProgress(progress),
        { skipAnalysis: false, purpose: 'BEST_PRACTICE' }
      );

      const successCount = result.success.length;
      const uploadErrors = result.failed.map((f) => `${f.filename}: ${f.error}`);

      if (successCount > 0) {
        queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId] });
      }

      const allErrors = [...errors, ...uploadErrors];
      if (allErrors.length > 0) {
        if (successCount > 0) {
          setError(`${successCount}개 업로드 완료. 오류: ${allErrors.join(', ')}`);
        } else {
          setError(allErrors.join('\n'));
        }
      }
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    }

    setIsUploading(false);
    setUploadProgress([]);
    const input = e.target as HTMLInputElement;
    if (input) input.value = '';
  }, [inspectionId, companyId, inspection, queryClient]);

  // 사진별 검토 완료 토글
  const handlePhotoReview = useCallback(async (photoId: string, reviewed: boolean) => {
    try {
      await updatePhotoReviewMutation.mutateAsync({ photoId, isReviewed: reviewed });
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    }
  }, [updatePhotoReviewMutation]);

  // 일괄 검토 완료
  const handleBulkReview = useCallback(async () => {
    try {
      await bulkReviewPhotosMutation.mutateAsync();
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    }
  }, [bulkReviewPhotosMutation]);

  // 선택된 사진들 검토 완료
  const handleSelectiveReview = useCallback(async (photoIds: string[]) => {
    try {
      // 선택된 사진들을 순차적으로 검토 완료 처리
      for (const photoId of photoIds) {
        await updatePhotoReviewMutation.mutateAsync({ photoId, isReviewed: true });
      }
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    }
  }, [updatePhotoReviewMutation]);

  // 선택된 사진들 삭제 (호출 전 UI에서 확인 완료된 상태)
  const handleSelectiveDelete = useCallback(async (photoIds: string[]) => {
    if (!inspectionId || !companyId || photoIds.length === 0) return;

    try {
      // 여러 장인 경우 배치 삭제 API 사용 (중복 알림 방지)
      if (photoIds.length > 1) {
        const response = await deletePhotos(inspectionId, photoIds, companyId);
        if (!response.success) {
          setError(response.error?.message || '사진 삭제에 실패했습니다.');
        }
      } else {
        // 단일 삭제
        const response = await deletePhoto(inspectionId, photoIds[0], companyId);
        if (!response.success) {
          setError(response.error?.message || '사진 삭제에 실패했습니다.');
        }
      }
      queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId] });
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    }
  }, [inspectionId, companyId, queryClient]);

  // 점검 삭제
  const handleDeleteInspection = useCallback(async () => {
    if (!inspectionId || !companyId) return;

    if (!window.confirm('이 점검을 삭제하시겠습니까? 모든 사진과 발견사항이 함께 삭제됩니다.')) {
      return;
    }

    setIsDeleting(true);
    try {
      const response = await deleteInspection(inspectionId, companyId);
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['inspections'] });
        queryClient.invalidateQueries({ queryKey: ['inspectionSchedules'] });
        showToast('점검이 삭제되었습니다.', 'success');
        const date = inspection?.scheduledAt?.split('T')[0];
        const month = date?.substring(0, 7);
        const params = new URLSearchParams();
        if (month) params.set('month', month);
        if (date) params.set('date', date);
        navigate(`/schedules?${params.toString()}`);
      } else {
        setError(response.error?.message || '삭제에 실패했습니다.');
      }
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    } finally {
      setIsDeleting(false);
    }
  }, [inspectionId, companyId, queryClient, navigate, inspection, showToast]);

  // 이전 발견사항 해결
  const handleResolveFindings = useCallback(async (
    findingId: string,
    data: {
      resolutionPhotoUrl?: string;
      resolutionPhotoId?: string;
      resolutionNotes?: string;
    }
  ) => {
    try {
      await resolveWithEvidenceMutation.mutateAsync({ findingId, data });
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    }
  }, [resolveWithEvidenceMutation]);

  // 이전 발견사항 미해결로 되돌리기
  const handleUnresolveFindings = useCallback(async (findingId: string) => {
    if (!inspectionId || !companyId) return;

    try {
      const response = await unresolveFinding(inspectionId, findingId, companyId);
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId] });
        queryClient.invalidateQueries({ queryKey: ['previousFindings', inspectionId] });
      } else {
        setError(response.error?.message || '미해결로 되돌리기에 실패했습니다.');
      }
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    }
  }, [inspectionId, companyId, queryClient]);

  // 해결 사진 첨부 요청 (모달 열기)
  const handleRequestResolutionPhoto = useCallback((findingId: string) => {
    if (!inspectionId) return;
    openResolutionPhoto(inspectionId, findingId);
  }, [inspectionId, openResolutionPhoto]);

  // 사진 삭제 (호출 전 UI에서 확인 완료된 상태)
  const handlePhotoDelete = useCallback(async (photoId: string) => {
    if (!inspectionId || !companyId) return;

    try {
      const response = await deletePhoto(inspectionId, photoId, companyId);
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId] });
      } else {
        setError(response.error?.message || '사진 삭제에 실패했습니다.');
      }
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    }
  }, [inspectionId, companyId, queryClient]);

  /**
   * 사진 재분석.
   *
   * `options.analysisRegion`은 **세 상태를 그대로 넘긴다** — 미전달=저장된 영역 유지 /
   * `null`=해제(전체 분석) / 객체=교체. 그래서 영역 인자 없이 부르는 기존 호출부(메뉴의
   * 「다시 분석」)는 사용자가 지정해 둔 영역을 **건드리지 않는다**.
   *
   * ⚠️ 재분석은 그 사진의 발견사항을 전량 삭제하고 새로 만든다(사용자가 손댄 편집·개선대책
   * 선택도 사라진다) — 부르는 쪽이 그 사실을 알린 뒤에 불러야 한다.
   */
  const handlePhotoReanalyze = useCallback(async (
    photoId: string,
    options?: { analysisRegion?: AnalysisRegion | null },
  ) => {
    if (!inspectionId || !companyId) return;

    try {
      const response = await reanalyzePhoto(inspectionId, photoId, companyId, options);
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId] });
      } else {
        setError(response.error?.message || '재분석 요청에 실패했습니다.');
      }
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    }
  }, [inspectionId, companyId, queryClient]);

  // 선택된 사진들 재분석
  const handleBulkReanalyze = useCallback(async (photoIds: string[]) => {
    if (!inspectionId || !companyId || photoIds.length === 0) return;

    setIsReanalyzing(true);
    setError('');
    try {
      // 1장이면 개별 API, 2장 이상이면 bulk API 사용
      if (photoIds.length === 1) {
        const response = await reanalyzePhoto(inspectionId, photoIds[0], companyId);
        if (!response.success) {
          setError(response.error?.message || '재분석 요청에 실패했습니다.');
        }
      } else {
        // bulk API 사용 (서버에서 배치 처리 + 한 번에 STARTED 이벤트 발송)
        const response = await bulkReanalyzePhotos(inspectionId, photoIds, companyId);
        if (!response.success) {
          setError(response.error?.message || '재분석 요청에 실패했습니다.');
        }
      }
      // 재분석 요청 후 데이터 새로고침
      queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId] });
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    } finally {
      setIsReanalyzing(false);
    }
  }, [inspectionId, companyId, queryClient]);

  // 사진 다운로드
  const handlePhotoDownload = useCallback(async (photoId: string) => {
    const photo = inspection?.photos.find(p => p.id === photoId);
    if (!photo) return;

    try {
      // 이미지를 blob으로 가져오기
      const response = await fetch(photo.url);
      const blob = await response.blob();

      // 다운로드 링크 생성
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // 파일명 추출 (URL에서 또는 기본값 사용)
      const fileName = photo.url.split('/').pop() || `photo_${photoId}.jpg`;
      link.download = fileName;

      // 다운로드 트리거
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // blob URL 해제
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('사진 다운로드 실패:', err);
      // 실패 시 새 탭에서 열기 (fallback)
      window.open(photo.url, '_blank');
    }
  }, [inspection]);

  // 상세 화면의 "보고서 생성"은 각 레이아웃이 ReportTypeSelectModal을 직접 연다
  // (Desktop/Fold/Phone 모두 자체 showReportModal 상태 소유). 여기 있던 구 안전점검 직행
  // 핸들러는 세 레이아웃 어디서도 호출되지 않는 dead code였고 D7-S2(2026-08-18)에서 제거했다.

  // === 발견사항 편집 핸들러 ===

  // 발견사항 수정
  const handleUpdateFinding = useCallback(async (
    findingId: string,
    data: UpdateFindingInput,
    options?: { isRevert?: boolean }
  ) => {
    try {
      // isRevert 옵션이 있으면 data에 포함
      const requestData = options?.isRevert ? { ...data, isRevert: true } : data;
      await updateFindingMutation.mutateAsync({ findingId, data: requestData });
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
      throw err;
    }
  }, [updateFindingMutation]);

  // 개선대책 추가 (measureId가 있으면 법적 근거 연동)
  const handleAddMeasure = useCallback(async (findingId: string, measure: string, measureId?: string) => {
    try {
      await addMeasureMutation.mutateAsync({ findingId, measure, measureId });
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
      throw err;
    }
  }, [addMeasureMutation]);

  // 개선대책 삭제
  const handleRemoveMeasure = useCallback(async (findingId: string, index: number) => {
    try {
      await removeMeasureMutation.mutateAsync({ findingId, index });
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
      throw err;
    }
  }, [removeMeasureMutation]);

  // 관련법규 추가 (selectedMeasures: 피드백용 연결 대책)
  const handleAddLegalBasis = useCallback(async (
    findingId: string,
    legalBasis: { type: string; article?: string; summary: string; url?: string; regulationId?: string },
    selectedMeasures?: string[]
  ) => {
    try {
      await addLegalBasisMutation.mutateAsync({ findingId, legalBasis, selectedMeasures });
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
      throw err;
    }
  }, [addLegalBasisMutation]);

  // 관련법규 삭제
  const handleRemoveLegalBasis = useCallback(async (findingId: string, index: number) => {
    try {
      await removeLegalBasisMutation.mutateAsync({ findingId, index });
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
      throw err;
    }
  }, [removeLegalBasisMutation]);

  // 발견사항 삭제
  const handleDeleteFinding = useCallback(async (findingId: string) => {
    try {
      await deleteFindingMutation.mutateAsync(findingId);
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
      throw err;
    }
  }, [deleteFindingMutation]);

  // 발견사항 복원
  //
  // ⚠️ **형제 핸들러들과 달리 `apiErrorMessage`를 쓴다 — 의도다.** `extractApiError`는 응답
  // 본문에 `error`가 없으면 **axios raw 문구를 그대로 돌려준다**(`'Network Error'` 등). 그것이
  // 보통은 대수롭지 않지만 이 자리에서는 다르다:
  //
  //   ① 이 핸들러의 문구는 **사후 안내가 유일한 경로**다. 서버 `restoreFinding`의 사진 생존
  //      게이트(「…사진을 다시 올려 발견사항을 추가해 주세요」)는 **사전 disable이 구조적으로
  //      불가**하다 — 서버 `findOne`이 photos를 `visiblePhotoWhere()`로 걸러 내려주므로
  //      클라이언트는 사진 보관 사실을 알 수 없고, 그 게이트의 도달 경로가 **stale 화면 그
  //      자체**다. 그 한 번을 놓치면 사용자는 「눌렀는데 아무 일도 없었다」로 끝난다.
  //   ② 그 문구는 이제 `pages/inspections/[id].adaptive.tsx`의 `role="alert"` 토스트로
  //      **스크린리더에 단정적으로 읽힌다**. raw 영문이 그 자리에 실리면 안내가 아니라 소음이다.
  //
  // `apiErrorMessage`는 서버가 실제로 내려준 문구만 주고(없으면 `null`) 아래 폴백으로 떨어진다.
  // ⇒ 형제 핸들러들(`handleDeleteFinding` 등)은 아직 `extractApiError`다. 그쪽도 옮기는 것이
  //   옳지만 이 작업 범위가 아니다 — **여기만 먼저 옮긴 이유가 위 ①·②**이고, 통일할 때 이
  //   주석을 근거로 쓰면 된다.
  const handleRestoreFinding = useCallback(async (findingId: string) => {
    try {
      await restoreFindingMutation.mutateAsync(findingId);
    } catch (err) {
      setError(apiErrorMessage(err) ?? '발견사항을 복원하지 못했습니다. 잠시 후 다시 시도해 주세요.');
      throw err;
    }
  }, [restoreFindingMutation]);

  // === 검토 요청 핸들러 ===

  // 검토 요청
  const handleRequestReview = useCallback(async (): Promise<ReviewRequestResult | null> => {
    if (!inspectionId || !companyId) return null;

    try {
      const response = await apiRequestReview(inspectionId, companyId);
      if (response.success && response.data) {
        return response.data;
      }
      setError(response.error?.message || '검토 요청에 실패했습니다.');
      return null;
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
      return null;
    }
  }, [inspectionId, companyId]);

  // 발견사항 추가 (새 발견사항 ID 반환)
  const handleAddFinding = useCallback(async (
    photoId: string,
    data: {
      category: string;
      description: string;
    }
  ): Promise<string | undefined> => {
    if (!inspectionId || !companyId) return undefined;

    try {
      const response = await apiAddFinding(
        inspectionId,
        {
          photoId,
          category: data.category,
          description: data.description,
          aiGenerated: false,
          // 수동 추가 시 사진 중앙에 절반 크기의 위험 영역 설정 (0~1 정규화 좌표)
          boundingBox: {
            x: 0.25,
            y: 0.25,
            width: 0.5,
            height: 0.5,
          },
        },
        companyId
      );
      if (response.success && response.data) {
        queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId] });
        showToast('발견사항이 추가되었습니다.', 'success');
        return response.data.id; // 새 발견사항 ID 반환
      } else {
        throw new Error(response.error?.message || '발견사항 추가에 실패했습니다.');
      }
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
      throw err;
    }
  }, [inspectionId, companyId, queryClient, showToast]);

  // 카메라 촬영 사진 → pendingPhotos에 추가 후 시트 오픈
  const handleCameraPhotosUpload = useCallback(async (files: File[]) => {
    if (files.length === 0) return;

    try {
      const newPending: PendingPhoto[] = [];
      for (const file of files) {
        newPending.push(await createStablePreview(file));
      }
      setPendingPhotos((prev) => [...prev, ...newPending]);
      setPhotoUploadSheetOpen(true);
    } catch {
      showToast('사진을 읽을 수 없습니다. 다시 시도해주세요.', 'error');
    }
  }, [showToast]);

  // 새로고침 (모바일용)
  const handleRefresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId] });
    await queryClient.invalidateQueries({ queryKey: ['previousFindings', inspectionId] });
    await queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId, 'contributors'] });
  }, [queryClient, inspectionId]);

  // 예약 점검 시작 확인 모달 상태
  const [startScheduledConfirmOpen, setStartScheduledConfirmOpen] = useState(false);

  // 예약 점검 시작 버튼 클릭 → 모달 열기
  const handleStartScheduled = useCallback(() => {
    setStartScheduledConfirmOpen(true);
  }, []);

  // 모달에서 확인 → 실제 API 호출
  const handleConfirmStartScheduled = useCallback(async () => {
    if (!inspectionId || !companyId) return;
    setStartScheduledConfirmOpen(false);
    try {
      const response = await startScheduledInspection(inspectionId, companyId);
      if (response.success) {
        // 오늘 예정된 점검 위젯(대시보드)에서 즉시 제거 (WebSocket emit 누락/지연 대비)
        // 키는 `lib/inspections.ts`의 루트 헬퍼 — 리터럴을 적으면 그 문자열을 고칠 때 조용히
        // 아무것도 무효화하지 않는다(2026-10-05).
        queryClient.invalidateQueries({ queryKey: todayScheduledQueryRootKey() });
        await handleRefresh();
      } else {
        setError(response.error?.message || '점검 시작에 실패했습니다.');
      }
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    }
  }, [inspectionId, companyId, handleRefresh, queryClient]);

  // === 데이터 변환 ===

  // 발견사항을 레이아웃용 형식으로 변환
  const convertFindingToLayout = (finding: InspectionFinding): LayoutFinding => ({
    id: finding.id,
    category: finding.category,
    subCategory: finding.subCategory || undefined,
    description: finding.description,
    severity: finding.severity as Severity,
    aiGenerated: finding.aiGenerated,
    aiConfidence: finding.aiConfidence,
    aiAnalysisDetail: finding.aiAnalysisDetail,
    aiEdited: finding.aiEdited,
    modifications: finding.modifications,
    resolutionStatus: finding.resolutionStatus as 'PENDING' | 'COMPLIED' | 'NON_COMPLIED' | 'PARTIAL' | undefined,
    resolutionPhotoUrl: finding.resolutionPhotoUrl ? normalizeFileUrl(finding.resolutionPhotoUrl) : undefined,
    resolutionPhotos: finding.resolutionPhotos?.map((p: { url: string; thumbnailUrl?: string; fileId?: string }) => ({
      url: normalizeFileUrl(p.url) || p.url,
      thumbnailUrl: p.thumbnailUrl ? normalizeFileUrl(p.thumbnailUrl) : undefined,
      fileId: p.fileId,
    })),
    notes: finding.notes,
    deletedAt: finding.deletedAt,
    deletedBy: finding.deletedBy,
    selectedMeasures: finding.selectedMeasures,
    aiLikelihoodScore: finding.aiLikelihoodScore ?? finding.aiAnalysisDetail?.riskAssessment?.likelihood ?? null,
    aiSeverityScore: finding.aiSeverityScore ?? finding.aiAnalysisDetail?.riskAssessment?.severity ?? null,
    // TBM 분류 축 규약 ③ — 손나열 매핑이라 여기 빠지면 신필드가 화면에 도달하지 않는다
    analyzedWorkCategory: finding.analyzedWorkCategory ?? null,
    confirmedWorkCategory: finding.confirmedWorkCategory ?? null,
  });

  // 사진 데이터를 레이아웃용 형식으로 변환
  const convertPhotosToLayout = (): LayoutPhoto[] => {
    if (!inspection) return [];

    return inspection.photos.map((photo) => {
      // 해당 사진의 발견사항 찾기
      const photoFindings = inspection.findings
        .filter((f) => f.photoId === photo.id)
        .map(convertFindingToLayout);

      return {
        id: photo.id,
        url: normalizeFileUrl(photo.url),
        thumbnailUrl: normalizeFileUrl(photo.thumbnailUrl),
        status: 'COMPLETED' as PhotoStatus, // 이미 업로드 완료된 사진들
        analysisStatus: photo.analysisStatus as LayoutAnalysisStatus,
        findings: photoFindings,
        isReviewed: photo.isReviewed ?? false,
        addedById: photo.addedById,
        addedBy: photo.addedBy,
        purpose: photo.purpose,
        location: photo.location ?? null,
        detailLocation: photo.detailLocation ?? null,
        equipment: photo.equipment ?? null,
        processType: photo.processType ?? null,
        // 「범위 지정 후 다시 분석」 모달이 저장된 영역에서 시작하려면 이 줄이 있어야 한다.
        // 빠지면 모달이 늘 「범위 없음」으로 열리고, 그대로 확인하면 `null`이 전달돼
        // 지정해 둔 영역이 지워진다(2026-10-05 dev 실측으로 재현). 타입이 required라
        // 이 줄을 지우면 tsc가 깨진다(되돌림 프로브로 `TS2322` 확인).
        analysisRegion: photo.analysisRegion ?? null,
      };
    });
  };

  // 이전 발견사항을 레이아웃용 형식으로 변환
  const convertPreviousFindingsToLayout = (): LayoutPreviousFinding[] => {
    return previousFindings.map((finding: PreviousUnresolvedFinding) => ({
      id: finding.id,
      category: finding.category,
      subCategory: finding.subCategory || undefined,
      description: finding.description,
      severity: finding.severity as Severity,
      photoUrl: normalizeFileUrl(finding.photo?.url),
      thumbnailUrl: normalizeFileUrl(finding.photo?.thumbnailUrl),
      foundAt: finding.createdAt,
      resolutionStatus: finding.resolutionStatus,
      resolutionPhotoUrl: normalizeFileUrl(finding.resolutionPhotoUrl),
      resolutionPhotos: finding.resolutionPhotos?.map(p => ({
        url: normalizeFileUrl(p.url) || p.url,
        thumbnailUrl: p.thumbnailUrl ? normalizeFileUrl(p.thumbnailUrl) : undefined,
        fileId: p.fileId,
      })),
      notes: finding.notes,
      resolvedAt: finding.resolvedAt || undefined,
      lastActionInspectionId: finding.lastActionInspectionId,
      photoAddedById: finding.photo?.addedById,
      sourceInspectionId: finding.inspection?.id,
      photoId: finding.photoId || finding.photo?.id,
      aiAnalysisDetail: finding.aiAnalysisDetail,
      reportMeasure: finding.reportMeasure || finding.refinements?.[0]?.refinedMeasure,
      reportRisk: finding.reportRisk || finding.refinements?.[0]?.refinedRisk,
      aiLikelihoodScore: finding.aiLikelihoodScore ?? finding.aiAnalysisDetail?.riskAssessment?.likelihood ?? null,
      aiSeverityScore: finding.aiSeverityScore ?? finding.aiAnalysisDetail?.riskAssessment?.severity ?? null,
    }));
  };

  // 참여자 데이터를 레이아웃용 형식으로 변환
  const convertContributorsToLayout = (): LayoutContributor[] => {
    return contributors.map((p) => ({
      id: p.id,
      userId: p.userId,
      userName: p.userName || p.user?.member?.name || '',
      role: p.role,
      profileImageUrl: p.profileImageUrl,
      completionStatus: p.completionStatus,
      completedAt: p.completedAt ?? undefined,
      signerName: p.signerName ?? undefined,
      signatureData: p.signatureData ?? undefined,
      excludedAt: p.excludedAt ?? undefined,
      hasPhotoContribution: p.hasPhotoContribution,
      hasMeasureContribution: p.hasMeasureContribution,
    }));
  };

  // 전체 점검 데이터를 레이아웃용 형식으로 변환
  const layoutInspection: LayoutInspectionDetail | null = inspection
    ? {
        id: inspection.id,
        siteName: inspection.site.name,
        siteId: inspection.siteId,
        status: (inspection.status === 'COMPLETED' ? 'COMPLETED' : inspection.status === 'SCHEDULED' ? 'SCHEDULED' : 'IN_PROGRESS') as InspectionStatus,
        createdById: inspection.createdById,
        inspectorId: inspection.inspectorId,
        inspectorName: getEvidenceDisplayName(
          (() => {
            const primary = contributors.find((c: any) => c.role === 'PRIMARY') as any;
            return primary?.signerName ?? primary?.joinSnapshotName ?? null;
          })(),
          { name: inspection.inspector?.member?.name, deletedAt: inspection.inspector?.deletedAt },
          '-',
        ),
        contributors: convertContributorsToLayout(),
        collaborators: inspection.collaborators,
        photos: convertPhotosToLayout(),
        previousFindings: convertPreviousFindingsToLayout(),
        createdAt: inspection.createdAt,
        scheduledAt: inspection.scheduledAt,
        assignedById: inspection.assignedById ?? null,
        nextInspectionDate: inspection.nextInspectionDate ?? null,
        nextInspectionHasReport: inspection.nextInspectionHasReport ?? false,
        blockingInspectionId: inspection.blockingInspectionId,
        blockingInspectionOrder: inspection.blockingInspectionOrder,
        sequenceNumber: inspection.sequenceNumber,
        effectiveRiskAssessmentConfig: inspection.effectiveRiskAssessmentConfig ?? null,
      }
    : null;

  // 발견사항 편집 핸들러 객체 — 레이아웃(Desktop/Fold/Phone) → InspectionPhotoSection →
  // PhotoCardVertical → FindingDetail로 내려간다. 타입 정본은 shared/FindingDetail의
  // `FindingEditHandlers`이고 layouts/inspection-detail.types가 그것을 re-export한다.
  const findingEditHandlers = {
    onUpdateFinding: handleUpdateFinding,
    onAddMeasure: handleAddMeasure,
    onRemoveMeasure: handleRemoveMeasure,
    onAddLegalBasis: handleAddLegalBasis,
    onRemoveLegalBasis: handleRemoveLegalBasis,
    onDeleteFinding: handleDeleteFinding,
    onRestoreFinding: handleRestoreFinding,
  };

  // 읽기 전용 모드 여부 (비담당자 readonly 접근 또는 SCHEDULED 상태)
  const isScheduled = inspection?.status === 'SCHEDULED';
  const isReadonly = inspection?.accessType === 'readonly' || isScheduled;
  // 비담당자 읽기 전용 접근 여부 (조직 설정에 의한 readonly 접근)
  const isViewOnlyAccess = inspection?.accessType === 'readonly';
  // 점검 관리 권한(P4 ②) — 서버 `InspectionQueryHelper.resolveActor().canAct`와 같은 축:
  // 소유자 성질이면 전 현장, 그 외는 배정 현장 + `inspection.manage`(현장 오버라이드 적용). 레이아웃 prop 이름 `isOwner`는 호환용이다.
  // 개선사항 선택 랭크('OWNER')만은 소유자 성질을 따로 읽는다(EditableMeasureList).
  const { canManageInspections, isSiteAssigned, isOwner: isOrgOwner } = usePermissions(inspection?.siteId);
  const isOwner = isOrgOwner || (isSiteAssigned && canManageInspections);
  // 현재 사용자의 점검 참여 역할이 PRIMARY인지 여부 (measure 해제 override 권한용)
  const isPrimary = useMemo(
    () => contributors.some((c: any) => c.userId === currentUserId && c.role === 'PRIMARY'),
    [contributors, currentUserId],
  );
  // 점검 주 담당자 ID (권한 체크용)
  const inspectorId = inspection?.inspectorId;

  // 보고서 생성 여부
  const hasReport = inspection?.hasReport ?? false;

  // 이후 차수 점검 존재 여부 (최신 점검이 아닌 완료 점검은 수정 불가)
  const isLatestInspection = inspection?.isLatestInspection ?? true;
  const isLockedByLaterInspection = inspection?.status === 'COMPLETED' && !isLatestInspection;

  // 개선대책이 1건 이상 선택된 **살아 있는** 발견사항이 있는지
  //
  // ⚠️ **이 한 줄이 무엇을 여는지 — 좁게 읽지 말 것.** 이 값은 `useInspectionCompletion`으로
  // 넘어가 **점검완료 서명 게이트**가 된다(`canSign = isContributor && !isMyCompleted &&
  // hasAnyMeasures` · `signDisabledReason`의 「개선대책을 1건 이상 선택해주세요」). 느슨하게 세면
  // 서명 버튼이 활성이 되고 그 안내 문구가 **뜨지 않는다** — 지운 것으로 충족되기 때문이다.
  //   ⚠️ **짝이 하나 더 있다**: 「보고서 생성」 게이트는 이 값이 아니라 레이아웃 셋이 각자 세는
  //   `hasAnySelectedMeasure`다(`useInspectionCompletion`의 `canGenerateReport`는 `allCompleted`만
  //   본다 — 이 값을 읽지 않는다). 즉 **파생이 둘이고 게이트도 둘**이며, 공통 입력이 아래 공용
  //   술어다. 한쪽만 고치면 뷰포트·흐름에 따라 동작이 갈린다 — 2026-10-07에 그 둘이 함께 거짓이
  //   었다(13자리).
  //
  // ⚠️ **사전 결함 수정(2026-10-07).** 서버 `findOne`은 발견사항을 삭제분까지 **전수로 내려주는
  // 것이 계약**이다(복원 입구의 유일한 공급원 — 그 자리 주석 참조). 삭제분을 입력으로 쓰는
  // 「그리는 자리」(`PhotoCardVertical`)와는 반대 방향이니, 공용 술어(`lib/findingVisibility`)로
  // 모아 두 역할을 구분한다. 배선은 가드 spec
  // `lib/__tests__/findingVisibilityWiring.test.ts`가 지킨다.
  const hasAnyMeasures = useMemo(() => {
    if (!inspection) return false;
    return hasSelectedMeasure(inspection.findings);
  }, [inspection]);

  // 점검완료 서명 훅
  const completionHook = useInspectionCompletion({
    inspectionId,
    companyId,
    contributors: convertContributorsToLayout(),
    hasReport,
    hasAnyMeasures,
  });

  // 개선대책 보고서 포함 여부 토글
  const handleToggleMeasureSelection = useCallback(async (findingId: string, measure: string, selected: boolean) => {
    if (!inspectionId || !companyId) return;
    try {
      await toggleMeasureSelection(inspectionId, findingId, measure, selected, companyId);
      queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId] });
    } catch (err) {
      const apiError = extractApiError(err);
      showToast(apiError.message, 'error');
    }
  }, [inspectionId, companyId, queryClient, showToast]);

  return {
    // 데이터
    inspection: layoutInspection,
    isLoading,
    error: displayError,
    currentUserId,
    isAnalysisComplete,
    isReadonly,
    isScheduled,
    isViewOnlyAccess,
    hasReport,
    isLockedByLaterInspection,
    // 권한 체크용
    isOwner,
    isPrimary,
    inspectorId,
    // 현재 점검을 보고 있는 사용자 목록
    viewers,

    isUploading,
    uploadProgress,
    isDeleting,

    // AI 분석 옵션
    enableAIAnalysis,
    setEnableAIAnalysis,
    focusCategories,
    setFocusCategories,

    // 핸들러
    onBack: handleBack,
    onPhotoUpload: handlePhotoUpload,
    onBestPracticePhotoUpload: handleBestPracticePhotoUpload,
    onOpenCamera: openCameraCapture,
    onCameraPhotosUpload: handleCameraPhotosUpload,
    onPhotoReview: handlePhotoReview,
    onBulkReview: handleBulkReview,
    onSelectiveReview: handleSelectiveReview,
    onSelectiveDelete: handleSelectiveDelete,
    onBulkReanalyze: handleBulkReanalyze,
    onDeleteInspection: handleDeleteInspection,
    onResolveFindings: handleResolveFindings,
    onUnresolveFindings: handleUnresolveFindings,
    onRequestResolutionPhoto: handleRequestResolutionPhoto,
    onPhotoDelete: handlePhotoDelete,
    onPhotoReanalyze: handlePhotoReanalyze,
    onPhotoDownload: handlePhotoDownload,
    onRefresh: handleRefresh,

    // 검토 요청 핸들러
    onRequestReview: handleRequestReview,
    onStartScheduled: handleStartScheduled,
    startScheduledConfirmOpen,
    onConfirmStartScheduled: handleConfirmStartScheduled,
    onCancelStartScheduled: () => setStartScheduledConfirmOpen(false),

    // 발견사항 추가 핸들러
    onAddFinding: handleAddFinding,

    // 발견사항 편집 핸들러
    findingEditHandlers,

    // 발견사항 보고서 포함 선택
    findingSelectionEnabled: true,
    onToggleMeasureSelection: handleToggleMeasureSelection,

    // 로딩 상태
    isBulkReviewLoading: bulkReviewPhotosMutation.isPending,
    isPhotoReviewLoading: updatePhotoReviewMutation.isPending,
    isReanalyzing,

    // 점검완료 서명 워크플로우
    completion: completionHook,
    hasAnyMeasures,
    // 사진 업로드 시트 (pendingPhotos 기반)
    pendingPhotos,
    onPendingPhotosChange: setPendingPhotos,
    photoUploadSheetOpen,
    onPhotoUploadSheetClose: () => {
      // 시트 닫을 때 preview URL 정리
      pendingPhotos.forEach((p) => URL.revokeObjectURL(p.preview));
      setPendingPhotos([]);
      setPhotoUploadSheetOpen(false);
    },
    onConfirmUpload: handleConfirmUpload,
  };
}
